import React, { useState, useCallback, useEffect } from 'react';
import { useMediaQuery } from '@react-hook/media-query';
import { cn } from './utility';
import SideMenu from './SideMenu';
import { useFlag } from '@openfeature/react-sdk';
const defaultWidth = 'w-16';
const expandedWidth = 'w-271';

export default function Sidebar({
    // the items to render at the top of the sidebar
    menuItems,
    // the items to render at the bottom of the sidebar
    bottomMenuItems,
    // whether to force the sidebar to collapse
    collapseSidebar,
    // the ID of the active applications in the sidebar
    activeApp,
    activeSubnavItem,
    currentClient,
    navigateToUrl,
}) {
    const isLargeScreen = useMediaQuery('(min-width: 1920px)');
    const isAlwaysOpen = isLargeScreen && !collapseSidebar;
    const [isOpen, setOpen] = useState(false);
    const { value: genDashbordFlag } = useFlag('generative-dashboards', false);

    if (!genDashbordFlag) {
        menuItems = menuItems.map(item => {
            if (item.id === 'dashboards') {
                return {
                    ...item,
                    subNav: [],
                    subNavItems: null,
                };
            }
            return item;
        });
    }

    // wrapping up navigateToUrl so we can close the menu on click before navigating
    // we don't need to worry about this on non single UI apps since it triggers
    // a full page reload anyway.
    const closeMenuAndNavigate = useCallback(
        event => {
            setOpen(false);
            navigateToUrl(event);
        },
        [setOpen, navigateToUrl],
    );

    // on initial loading this may be null and we don't want to show anything
    // if the client is null.
    if (!currentClient) {
        return null;
    }

    // there is a lot of div nesting here to accomplish the fixed position stuff
    // while still maintaining the sidebar's position in the dom so we don't need
    // dynamic padding or margins in the main content area.
    // The parent container in alli-frontend-root has a height and we're filling
    // that height up here.
    // 1. control the width of the sidebar in cases where it's "always open" (eg large screens)
    // 2. Allow the inner child to be "fixed" position with its own width so the slide
    //    out effect works, but the sidebar itself still has a width in the layout.

    return (
        <div
            className={cn('relative h-full hidden md:block', {
                [defaultWidth]: !isAlwaysOpen,
                [expandedWidth]: isAlwaysOpen,
            })}
        >
            <div
                className={cn(
                    'relative h-full max-w-full z-20 transition-all duration-500',
                    {
                        'hover:z-50 ': !isAlwaysOpen,
                    },
                )}
                onMouseEnter={() => setOpen(true)}
                onMouseLeave={() => setOpen(false)}
            >
                <div
                    style={{ top: 60 }}
                    className={cn(
                        `fixed bottom-0 transition-all duration-500 dark:bg-blue-gray-700 bg-blue-gray-50 border-r border-gray-400 z-20 ${defaultWidth}`,
                        {
                            [expandedWidth]: isAlwaysOpen || isOpen,
                        },
                    )}
                    aria-hidden="true"
                >
                    <div className="flex flex-col justify-between h-full px-2 py-4 overflow-x-hidden overflow-y-auto">
                        <SideMenu
                            menuItems={menuItems}
                            bottomMenuItems={bottomMenuItems}
                            currentClient={currentClient}
                            activeApp={activeApp}
                            activeSubnavItem={activeSubnavItem}
                            navigateToUrl={closeMenuAndNavigate}
                            isSidebarOpen={isAlwaysOpen || isOpen}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}
